import React, { useEffect, useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000BasicSearch, { SEARCHTAB } from '../../../u2000Core/components/tables/basic/U2000BasicSearch';
import { type U2000SearchPayload, type U2000SearchResult } from '../../../u2000Core/components/tables/basic/U2000BasicTable';
import { type U2000TableColumn } from '../../../u2000Core/components/tables/basic/U2000BasicTableList';
import { u2000DefaultSortOrderForColumn } from '../../../u2000Core/tools/U2000DefaultSortOrderForColumn';
import { U2000HandleApiError } from '../../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { useU2000SearchHandlers } from '../../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000LogicalUnitContext } from '../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import {
  U2000_CRITERIA_LOOKUP_FIND_PROJECT,
  defaultItemToDisplay,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from '../../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000YesNoDialog from '../../../u2000Core/components/dialog/U2000YesNoDialog';
import U2000Panel from '../../../u2000Core/components/layout/U2000Panel';
import { NS, U3147ROLES } from '../../U3147';
import { useU3147Context } from '../../U3147Context';
import { useU3147ExportHandlers } from '../../U3147UseExportHandlers';
import { useU3147FindProjectHandlers } from '../../U3147UseFindProjectSubprojectHandlers';
import {
  doSearch,
  luHandleApiError,
  u3147FormatProjectLabel,
  u3147FormatSubprojectLabel,
  u3147FormatTaskLabel,
  u3147IsAdmIncludeSubProjectEnabled,
  type U3147TimeSessionSearchResult,
} from '../../U3147Service';
import { u3147FormatMinutesAsHoursMinutes, u3147FormatTimeFromIso } from '../../tools/u3147FormatDuration';
import { useU3147EnrichSessionsTasks } from '../../U3147UseEnrichSessionsTasks';
import { u3147GroupSessionsByDay } from '../../tools/u3147GroupSessionsByDay';
function getColumnsDef(t: TFunction, showEmployee: boolean): U2000TableColumn[] {
  const cols: U2000TableColumn[] = [
    {
      name: 'workDate',
      label: t('lblDate', { ns: NS, defaultValue: 'Date' }),
      widthClass: 'w-[120px]',
      type: 'date',
      align: 'left',
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
      defaultSortOrder: 'DESC',
      includeConditions: ['interval', 'equals', 'greaterthan', 'lessthan', 'notequals'],
    },
    {
      name: 'project.projetNumber',
      label: t('lblProject', { ns: NS }),
      widthClass: 'w-[160px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: true,
      sortable: true,
      includeConditions: ['equals', 'in'],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_FIND_PROJECT,
        searchCriterionField: 'projetNumber',
        valueKey: 'projetNumber',
        criteriaField: 'project.projetNumber',
        displayKeys: ['projetNumber', 'projetName'],
        selectedDisplayKeys: ['projetNumber', 'projetName'],
        placeholder: t('phCriteriaProject', { ns: 'common' }),
      },
    },
  ];

  if (showEmployee) {
    cols.push({
      name: 'employeeId',
      label: t('lblEmployee', { ns: NS, defaultValue: 'Employé' }),
      widthClass: 'w-[160px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: true,
      sortable: true,
    });
  }

  return cols;
}

const U3147HistoryGroupedList: React.FC<{
  rows: U3147TimeSessionSearchResult[];
  onExportDay: (date: string) => void;
  exportBusy?: boolean;
  showSubprojectColumn: boolean;
}> = ({ rows, onExportDay, exportBusy, showSubprojectColumn }) => {
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const canExport = hasRole(U3147ROLES.EXPORT);
  const enrichedRows = useU3147EnrichSessionsTasks(rows);
  const groups = useMemo(() => u3147GroupSessionsByDay(enrichedRows), [enrichedRows]);

  if (groups.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-500">
        {t('msgEmptyHistory', { ns: NS, defaultValue: 'Aucun résultat — lancez une recherche.' })}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.workDate} className="rounded-lg border border-gray-200 bg-white">
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">{group.workDate}</h3>
              <p className="text-xs text-gray-600">
                {t('lblTotalDay', { ns: NS })} : {u3147FormatMinutesAsHoursMinutes(group.totalMinutes)} ·{' '}
                {group.allExported
                  ? t('lblDayExported', { ns: NS, defaultValue: 'Exportée' })
                  : t('lblDayNotExported', { ns: NS, defaultValue: 'Non exportée' })}
              </p>
            </div>
            {canExport && group.hasUnexported && (
              <U2000ActionBtn
                label={t('btnExportThisDay', { ns: NS, defaultValue: 'Exporter ce jour' })}
                handleClick={() => onExportDay(group.workDate)}
                disabled={exportBusy}
              />
            )}
          </header>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead>
                <tr className="bg-white">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colStart', { ns: NS })}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colEnd', { ns: NS })}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colDuration', { ns: NS })}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colProject', { ns: NS })}</th>
                  {showSubprojectColumn && (
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colSubproject', { ns: NS })}</th>
                  )}
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colTask', { ns: NS })}</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">{t('colNote', { ns: NS })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {group.sessions.map((session) => (
                  <tr key={session.id}>
                    <td className="px-3 py-2">{u3147FormatTimeFromIso(session.startedAt)}</td>
                    <td className="px-3 py-2">{u3147FormatTimeFromIso(session.endedAt)}</td>
                    <td className="px-3 py-2">{u3147FormatMinutesAsHoursMinutes(session.durationMinutes ?? undefined)}</td>
                    <td className="px-3 py-2">{u3147FormatProjectLabel(session.project as never)}</td>
                    {showSubprojectColumn && (
                      <td className="px-3 py-2">{u3147FormatSubprojectLabel(session.subproject as never)}</td>
                    )}
                    <td className="px-3 py-2">{u3147FormatTaskLabel(session.task as never, i18n.language)}</td>
                    <td className="px-3 py-2">{session.note ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
};

const U3147HistoryView: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const axios = useU2000AxiosAuthenticated();
  const { hasRole } = useU2000AuthContext();
  const { searchStateHandlers, gridStateHandlers, initLU } = useU3147Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const findProjectHandlers = useU3147FindProjectHandlers();
  const exportHandlers = useU3147ExportHandlers();
  const showEmployee = hasRole(U3147ROLES.OTHER_EMPLOYEE);
  const showSubprojectColumn = u3147IsAdmIncludeSubProjectEnabled(initLU.personalization);
  const [searchTab, setSearchTab] = React.useState(SEARCHTAB.CRITERIAS);
  const [hideSearch, setHideSearch] = React.useState(false);

  useEffect(() => {
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_FIND_PROJECT, {
      search: findProjectHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
    });
    return () => unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_FIND_PROJECT);
  }, [findProjectHandlers.search]);

  useEffect(() => {
    const columns = getColumnsDef(t, showEmployee);
    searchStateHandlers.setColumns(columns);
    gridStateHandlers.setColumns(columns);
    setLogicalUnitBreadcrumb([{ label: t('title', { ns: NS }) }, { label: t('tabHistory', { ns: NS }) }]);

    const currentFields = searchStateHandlers.getFields();
    if (currentFields.length === 0) {
      searchStateHandlers.setFields(
        columns.filter((col) => col.isDefaultColumn).map((col) => ({ field: { ...col } })),
      );
    }
    const currentSortedFields = searchStateHandlers.getSortedFields();
    if (currentSortedFields.length === 0) {
      searchStateHandlers.setSortedFields(
        columns
          .filter((col) => col.isDefaultSortedColumn)
          .map((col) => ({ field: { ...col }, sortOrder: u2000DefaultSortOrderForColumn(col) })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init colonnes / breadcrumb (calque U3129TimesheetList)
  }, [t, showEmployee]);

  const handleSearch = useU2000SearchHandlers<U3147TimeSessionSearchResult>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => doSearch(axios, searchPayload),
    onSuccess: (searchResult: U2000SearchResult<U3147TimeSessionSearchResult>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const runSearch = (page = 1) => {
    handleSearch.handleSearch(
      searchStateHandlers.extractSearchParameter(),
      gridStateHandlers.extractPagination(page),
      U2000GridStateEnumPaginationDirection.NEXTPAGE,
    );
  };

  const rows = gridStateHandlers.getData() as U3147TimeSessionSearchResult[];

  if (searchStateHandlers.getColumns().length === 0) return null;

  return (
    <U2000Panel className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
      <section className="form-card animate-fade-in mb-4">
        <div className="form-card-body">
          <U2000BasicSearch
            searchStateHandlers={searchStateHandlers}
            defaultTab={searchTab}
            handleChangeTab={setSearchTab}
            handleShowSearch={(tab) => {
              if (!hideSearch && tab === searchTab) setHideSearch(true);
              else {
                setHideSearch(false);
                setSearchTab(tab ?? SEARCHTAB.CRITERIAS);
              }
            }}
            onCollapseSearch={() => setHideSearch(true)}
            isCollapsed={hideSearch}
            showCollapsedCriteriaChips
            onFilterChange={() => runSearch(1)}
            visibleSearchTabs={[SEARCHTAB.CRITERIAS]}
          />
        </div>
      </section>

      <p className="text-sm text-gray-600">
        {t('lblResultCount', { ns: NS, count: gridStateHandlers.getTotalRows(), defaultValue: '{{count}} plage(s)' })}
      </p>

      <U3147HistoryGroupedList
        rows={rows}
        exportBusy={exportHandlers.busy}
        onExportDay={(date) => exportHandlers.requestExport({ date })}
        showSubprojectColumn={showSubprojectColumn}
      />

      <U2000YesNoDialog
        isOpen={exportHandlers.confirmOpen}
        onClose={(result) => {
          if (result === 'yes') {
            void exportHandlers.executeExport().then((res) => {
              if (res) {
                handleSearch.handleSearch(
                  searchStateHandlers.extractSearchParameter(),
                  gridStateHandlers.extractPagination(gridStateHandlers.getCurrentPage()),
                  U2000GridStateEnumPaginationDirection.NEXTPAGE,
                );
              }
            });
          } else {
            exportHandlers.cancelExport();
          }
        }}
        title={t('lblExportConfirmTitle', { ns: NS, defaultValue: 'Exporter la journée' })}
      >
        {t('msgExportConfirm', {
          ns: NS,
          date: exportHandlers.pendingExport?.date ?? '',
          defaultValue: 'Confirmer l\'export des plages clôturées du {{date}} vers votre feuille de temps ?',
        })}
      </U2000YesNoDialog>
    </U2000Panel>
  );
};

export default U3147HistoryView;
